function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import { richTextValidation } from 'validators';
import { Controller, useForm, FormProvider } from 'react-hook-form';
import { Button, CustomModal, FullPageModal, RichTextEditor, TextField, Typography, useOpenUnsavedChangesModal } from 'seagull';
import { connectControllerToRichTextEditor, connectControllerToTextField } from "../../../utils/react-hook-form.utils";
import { useStore, useStoreActions } from "../../../contexts";
import { selectors } from "../../../store/state-selectors";
import { getPriceAfterDiscount, serviceToInput, outputToService, getDefaultServiceMapping, isNameFieldDisabled } from "./ServiceBuilder.utils";
import BillingTriggerDropdown from "./BillingTriggerDropdown";
import DiscountInput from "./DiscountInput";
import PriceTextField from "./PriceTextField";
import MinPriceTextField from "./MinPriceTextField";
import UnitNameTextField from "./UnitNameTextField";
import PreapprovedTextField from "./PreapprovedTextField";
import PricingTextField from "./PricingTextField";
import OccurrenceTextField from "./OccurrenceTextField";
import styles from "./ServiceBuilder.module.scss";
import { DiscountType, serviceModel } from "../../../models";
import { handlePromise } from "../../../infra/utils";
import ServiceQboField from "./ServiceQboField";
import PMMapServiceField from "../../../integrations/PracticeManagement/PMMapServiceField/PMMapServiceField";
const rootTestid = 'service-builder';
const formId = 'service-builder-form';
const defaultInitialState = {
  serviceName: '',
  description: '',
  recurrencePeriod: 'onetime',
  pricing: 'fixed',
  price: '',
  minPrice: '',
  unitName: '',
  unitCap: '',
  trigger: 'approval',
  discount: {
    type: DiscountType.Percent,
    amount: ''
  },
  serviceMapping: {
    type: 'unmapped'
  },
  financialCentsMapping: [],
  karbonMapping: [],
  clientHubMapping: []
};
export default function ServiceBuilder(props) {
  const {
    agreementData,
    service,
    serviceNameBlacklist,
    serviceOrigin,
    applyOutputToChanges,
    onSave,
    dismissModal,
    registerCanDismiss
  } = props;
  const {
    fetchQboItems,
    fetchIntegrations,
    fetchKarbonItems,
    fetchClientHubItems,
    fetchFinancialCentsItems
  } = useStoreActions();
  const openUnsavedChangesModal = useOpenUnsavedChangesModal();
  const disableIntegrationsMapping = !!service && serviceOrigin !== 'template';
  const fullPageModal = !service || serviceOrigin === 'template';
  const showQboIntegration = useStore(selectors.integration.showQboIntegration);
  const showKarbonIntegration = useStore(selectors.integration.showKarbonIntegration);
  const showClientHubIntegration = useStore(selectors.integration.showClientHubIntegration);
  const showFinancialCentsIntegration = useStore(selectors.integration.showFinancialCentsIntegration);
  const qboItems = useStore(selectors.integration.qboItems);
  const financialCentsItems = useStore(selectors.integration.financialCentsItems);
  const karbonItems = useStore(selectors.integration.karbonItems);
  const clientHubItems = useStore(selectors.integration.clientHubItems);
  const [saving, setSaving] = useState(false);
  const headerText = "".concat(service ? 'Edit' : 'Create', " Service");
  const onClose = () => dismissModal();
  const defaultValues = useMemo(() => {
    return service ? serviceToInput(service, (qboItems === null || qboItems === void 0 ? void 0 : qboItems.items) || []) : defaultInitialState;
  }, [service, qboItems]);
  const form = useForm({
    mode: 'all',
    defaultValues
  });
  const isFormDirty = form.formState.isDirty;
  const [pricing, price, minPrice, discount] = form.watch(['pricing', 'price', 'minPrice', 'discount']);
  useEffect(() => {
    const serviceMapping = getDefaultServiceMapping(service === null || service === void 0 ? void 0 : service.integratedId, (qboItems === null || qboItems === void 0 ? void 0 : qboItems.items) || []);
    form.setValue('serviceMapping', serviceMapping);
  }, [qboItems, service === null || service === void 0 ? void 0 : service.integratedId]);
  useEffect(() => {
    if (showQboIntegration || showFinancialCentsIntegration || showKarbonIntegration) fetchIntegrations();
    if (showQboIntegration) fetchQboItems();
    if (showFinancialCentsIntegration) handlePromise(fetchFinancialCentsItems());
    if (showKarbonIntegration) handlePromise(fetchKarbonItems());
    if (showClientHubIntegration) handlePromise(fetchClientHubItems());
  }, [showQboIntegration, showFinancialCentsIntegration, showKarbonIntegration]);
  const discountedPrice = useMemo(() => {
    return getPriceAfterDiscount({
      pricing,
      price,
      minPrice,
      discount
    });
  }, [pricing, price, minPrice, discount]);
  const onSubmit = async value => {
    if (!isFormDirty) {
      // Do nothing if form hasn't changed.
      return onClose();
    }
    const outputService = outputToService(value, service, {
      applyOutputToChanges,
      agreementData
    });
    if (onSave) {
      setSaving(true);
      const result = await onSave(outputService);
      setSaving(false);
      if (!result.success) return;
    }
    dismissModal(outputService, {
      force: true
    });
  };
  registerCanDismiss(async () => {
    if (!isFormDirty) {
      return true;
    }
    const confirmTitle = service ? 'Discard changes' : 'Cancel service';
    return openUnsavedChangesModal({
      testid: 'sb-confirm-cancel',
      title: confirmTitle,
      primaryButtonLabel: confirmTitle,
      content: 'Unsaved changes will be lost. To save the changes, complete the service details and click Save and exit.'
    });
  });
  const actionButtons = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, {
    skin: "primaryOutline",
    "data-testid": "sb-cancel",
    onClick: onClose
  }, "Cancel"), /*#__PURE__*/React.createElement(Button, {
    type: "submit",
    "data-testid": "sb-submit",
    form: formId,
    loading: saving
  }, "Save"));
  const content = /*#__PURE__*/React.createElement(FormProvider, form, /*#__PURE__*/React.createElement("form", {
    id: formId,
    className: styles.content,
    noValidate: true,
    onSubmit: form.handleSubmit(onSubmit)
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.leftSection
  }, /*#__PURE__*/React.createElement(Controller, {
    control: form.control,
    name: "serviceName",
    rules: {
      validate: n => serviceModel.validateName(n, serviceNameBlacklist)
    },
    render: renderProps => /*#__PURE__*/React.createElement("div", {
      className: styles.rowGap
    }, /*#__PURE__*/React.createElement(TextField, _extends({}, connectControllerToTextField(renderProps), {
      autoFocus: true,
      disabled: isNameFieldDisabled(service, serviceOrigin),
      label: "Service name",
      "data-testid": "sb-name"
    })))
  }), /*#__PURE__*/React.createElement(Typography, {
    variant: "paragraph2",
    skin: "secondary"
  }, "Service description"), /*#__PURE__*/React.createElement(Controller, {
    control: form.control,
    name: "description",
    rules: richTextValidation(),
    render: renderProps => /*#__PURE__*/React.createElement(RichTextEditor, _extends({
      editorHeight: 310,
      placeholder: "Describe service (Optional)",
      "data-testid": "sb-description"
    }, connectControllerToRichTextEditor(renderProps)))
  })), /*#__PURE__*/React.createElement("div", {
    className: styles.sep
  }), /*#__PURE__*/React.createElement("div", {
    className: styles.rightSection
  }, /*#__PURE__*/React.createElement("div", {
    className: cn(styles.topRightSection, styles.rowGap)
  }, /*#__PURE__*/React.createElement(OccurrenceTextField, {
    serviceOrigin: serviceOrigin,
    service: service
  }), /*#__PURE__*/React.createElement(PricingTextField, null), pricing === 'unit' && /*#__PURE__*/React.createElement(UnitNameTextField, null), pricing === 'range' && /*#__PURE__*/React.createElement(MinPriceTextField, null), /*#__PURE__*/React.createElement(PriceTextField, null), /*#__PURE__*/React.createElement(DiscountInput, null), ['hour', 'unit'].includes(pricing) && /*#__PURE__*/React.createElement(PreapprovedTextField, null)), discountedPrice && /*#__PURE__*/React.createElement(Typography, {
    className: styles.rowGap,
    "data-testid": "sb-discounted-price"
  }, "Price after discount: ", discountedPrice), /*#__PURE__*/React.createElement("div", {
    className: styles.rowGap
  }, /*#__PURE__*/React.createElement(BillingTriggerDropdown, {
    serviceOrigin: serviceOrigin,
    service: service
  })), showQboIntegration && /*#__PURE__*/React.createElement("div", {
    className: styles.rowGap
  }, /*#__PURE__*/React.createElement(ServiceQboField, {
    qboItems: (qboItems === null || qboItems === void 0 ? void 0 : qboItems.items) || null,
    isDisabled: disableIntegrationsMapping,
    testid: "qbo-mapping"
  })), showFinancialCentsIntegration && /*#__PURE__*/React.createElement("div", {
    className: styles.rowGap
  }, /*#__PURE__*/React.createElement(Controller, {
    name: "financialCentsMapping",
    control: form.control,
    render: _ref => {
      let {
        field: {
          value,
          onChange
        }
      } = _ref;
      return /*#__PURE__*/React.createElement(PMMapServiceField, {
        testid: "financial-cents-mapping",
        label: "Financial Cents mapping",
        options: financialCentsItems,
        loading: !financialCentsItems,
        disabled: disableIntegrationsMapping,
        tooltip: disableIntegrationsMapping ? 'Financial Cents items can be mapped through the services library' : '',
        value: value,
        onChange: onChange
      });
    }
  })), showKarbonIntegration && /*#__PURE__*/React.createElement("div", {
    className: styles.rowGap
  }, /*#__PURE__*/React.createElement(Controller, {
    name: "karbonMapping",
    control: form.control,
    render: _ref2 => {
      let {
        field: {
          value,
          onChange
        }
      } = _ref2;
      return /*#__PURE__*/React.createElement(PMMapServiceField, {
        testid: "karbon-mapping",
        label: "Karbon mapping",
        options: karbonItems,
        loading: !karbonItems,
        disabled: disableIntegrationsMapping,
        tooltip: disableIntegrationsMapping ? 'Karbon items can be mapped through the services library' : '',
        value: value,
        onChange: onChange
      });
    }
  })), showClientHubIntegration && /*#__PURE__*/React.createElement(Controller, {
    name: "clientHubMapping",
    control: form.control,
    render: _ref3 => {
      let {
        field: {
          value,
          onChange
        }
      } = _ref3;
      return /*#__PURE__*/React.createElement(PMMapServiceField, {
        testid: "client-hub-mapping",
        label: "Client Hub mapping",
        options: clientHubItems,
        loading: !clientHubItems,
        disabled: disableIntegrationsMapping,
        tooltip: disableIntegrationsMapping ? 'Client Hub items can be mapped through the services library' : '',
        value: value,
        onChange: onChange
      });
    }
  }))));
  return fullPageModal ? /*#__PURE__*/React.createElement(FullPageModal, {
    showFooter: true,
    "data-testid": rootTestid,
    "data-fullpage": fullPageModal,
    onClose: onClose
  }, /*#__PURE__*/React.createElement(FullPageModal.HeaderStart, null, headerText), content, /*#__PURE__*/React.createElement(FullPageModal.FooterEnd, null, actionButtons)) : /*#__PURE__*/React.createElement(CustomModal, {
    width: 1200,
    maxWidth: "90%",
    "data-testid": rootTestid,
    "data-fullpage": fullPageModal,
    header: /*#__PURE__*/React.createElement(Typography, {
      variant: "h6",
      weight: "bold"
    }, headerText),
    headerClassName: styles.modalHeader,
    footer: /*#__PURE__*/React.createElement("div", {
      className: styles.modalFooter
    }, /*#__PURE__*/React.createElement(Typography, null, "*Changes you make to this service will apply only to this agreement"), /*#__PURE__*/React.createElement("div", {
      className: styles.modalFooterEnd
    }, actionButtons)),
    onClose: onClose
  }, content);
}